
/**
 * UrlResolver provides utilities for resolving URLs relative to the component's base
 */
export class UrlResolver {
  private baseUrl: string = '';
  
  constructor(baseUrl: string = '') {
    this.baseUrl = baseUrl || '';
  }
  
  /**
   * Determine the base URL of where this component is hosted
   */
  determineBaseUrl(): string {
    try {
      // Find the script tag that loaded this component
      const scripts = document.querySelectorAll('script');
      let scriptUrl = '';
      
      // Try to find our script - look specifically for index-HASH.js pattern
      for (let i = 0; i < scripts.length; i++) {
        const src = scripts[i].src || '';
        // Look for our main JS file with hash pattern
        if (src.includes('index-') && src.includes('.js')) {
          scriptUrl = src;
          console.log('Found script with hash pattern:', src);
          break;
        }
      }
      
      // If we didn't find the hash pattern, look for any index.js
      if (!scriptUrl) {
        for (let i = 0; i < scripts.length; i++) {
          const src = scripts[i].src || '';
          if (src.includes('index.js') || src.includes('index.ts')) {
            scriptUrl = src;
            console.log('Found script with index pattern:', src);
            break;
          }
        }
      }
      
      if (scriptUrl) {
        // Extract the base URL from the script URL (everything up to the last slash)
        const urlParts = scriptUrl.split('/');
        urlParts.pop(); // Remove the JS filename
        this.baseUrl = urlParts.join('/') + '/';
        console.log('Script URL found:', scriptUrl);
        console.log('Base URL determined from script:', this.baseUrl);
      } else {
        // Special handling for development environment
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          this.baseUrl = window.location.origin + '/';
          console.log('Development environment detected, using current origin as base URL');
        } else {
          // For production without finding script, fall back to a hardcoded URL
          // This is a last resort to ensure our component can load resources
          this.baseUrl = 'https://meet2.onexip.com/';
          console.log('Could not determine script URL, falling back to hardcoded URL:', this.baseUrl);
        }
      }
      
      console.log('Web component base URL determined:', this.baseUrl);
      return this.baseUrl;
    } catch (error) {
      console.error('Error determining base URL:', error);
      // Ultimate fallback
      this.baseUrl = 'https://meet2.onexip.com/';
      console.warn('Error in base URL detection, using fallback URL:', this.baseUrl);
      return this.baseUrl;
    }
  }
  
  /**
   * Resolve a relative URL against the component's base URL
   */
  resolveUrl(relativeUrl: string): string {
    try {
      // If it's already an absolute URL, return as is
      if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
        return relativeUrl;
      }
      
      // Special handling for lovable-uploads paths to ensure they're loaded from our server
      if (relativeUrl.includes('lovable-uploads/')) {
        // Always use the component's base URL for lovable-uploads resources
        const filename = relativeUrl.split('lovable-uploads/').pop();
        if (!filename) return this.baseUrl + relativeUrl;
        
        const absoluteUrl = `${this.baseUrl}lovable-uploads/${filename}`;
        console.log(`Resolved lovable-upload from ${relativeUrl} to: ${absoluteUrl}`);
        return absoluteUrl;
      }
      
      // Remove leading ./ if present
      const cleanUrl = relativeUrl.startsWith('./') ? relativeUrl.substring(2) : relativeUrl;
      
      // Join with base URL - ensuring absolute path
      const absoluteUrl = this.baseUrl + cleanUrl;
      console.log(`Resolved URL from ${relativeUrl} to: ${absoluteUrl}`);
      return absoluteUrl;
    } catch (error) {
      console.error('Error resolving URL:', error);
      return this.baseUrl + relativeUrl;
    }
  }
  
  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}
